import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import Nav from './components/Nav';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import NextUp from './components/NextUp';
import Home from './pages/Home';
import About from './pages/About';
import WhatsOn from './pages/WhatsOn';
import EventDetail from './pages/EventDetail';
import Menus from './pages/Menus';
import Bookings from './pages/Bookings';
import Contact from './pages/Contact';
import LocationDetail from './pages/LocationDetail';
import NotFound from './pages/NotFound';
import './App.css';

/** Pages that open on a dark, full-bleed hero need an inverted header. */
const LIGHT_HERO_ROUTES = ['/menus'];

/** /theme-name/<slug> and /location/<slug> were the WordPress URLs. */
function LegacyRedirect({ to }) {
  const { slug } = useParams();
  return <Navigate to={`${to}/${slug}`} replace />;
}

export default function App() {
  const { pathname, key } = useLocation();

  useEffect(() => {
    document.body.classList.toggle('has-dark-hero', !LIGHT_HERO_ROUTES.includes(pathname));
  }, [pathname]);

  // Fade images in as they finish loading — mirrors the WordPress site's
  // lazy-load fade (LiteSpeed) without per-component wiring. `load` doesn't
  // bubble, so listen in the capture phase.
  useEffect(() => {
    const onLoad = (e) => {
      if (e.target instanceof HTMLImageElement) e.target.classList.add('is-loaded');
    };
    document.addEventListener('load', onLoad, true);
    return () => document.removeEventListener('load', onLoad, true);
  }, []);

  // Cache hits can complete before the listener exists (first paint, route
  // changes) — sweep and mark anything already done.
  useEffect(() => {
    const sweep = () => {
      document.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) img.classList.add('is-loaded');
      });
    };
    sweep();
    const raf = window.requestAnimationFrame(sweep);
    return () => window.cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <ScrollToTop />
      <Nav />

      <main id="main" className="app__main" key={key}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />

          <Route path="/whats-on" element={<WhatsOn />} />
          <Route path="/whats-on/:slug" element={<EventDetail />} />

          <Route path="/menus" element={<Menus />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/locations" element={<Navigate to="/#locations" replace />} />
          <Route path="/locations/:slug" element={<LocationDetail />} />

          {/* Legacy WordPress URLs — keep the inbound links and rankings alive. */}
          <Route path="/menu" element={<Navigate to="/menus" replace />} />
          <Route path="/theme-name/:slug" element={<LegacyRedirect to="/whats-on" />} />
          <Route path="/location/:slug" element={<LegacyRedirect to="/locations" />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <NextUp />
      <Footer />
    </>
  );
}
